import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(slug || 'self-identity');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === activeCategory);
  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter(a => a.categorySlug === activeCategory);

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <div className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t('category.title') },
              ]}
            />
          </div>

          {/* Category Header */}
          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
              {t('category.title')}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
              {t('category.desc')}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-12">
            <Link
              to="/category/self-identity"
              onClick={() => setActiveCategory('self-identity')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === 'self-identity'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              情感自我认同
            </Link>
            <Link
              to="/category/intimate-communication"
              onClick={() => setActiveCategory('intimate-communication')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === 'intimate-communication'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              亲密关系沟通
            </Link>
            <Link
              to="/category/emotional-confusion"
              onClick={() => setActiveCategory('emotional-confusion')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === 'emotional-confusion'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              情感表达困惑
            </Link>
            <Link
              to="/category/self-acceptance"
              onClick={() => setActiveCategory('self-acceptance')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === 'self-acceptance'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              自我接纳方法
            </Link>
            <Link
              to="/category/couple-skills"
              onClick={() => setActiveCategory('couple-skills')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === 'couple-skills'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              情侣沟通技巧
            </Link>
            <Link
              to="/category/emotional-needs"
              onClick={() => setActiveCategory('emotional-needs')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === 'emotional-needs'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              情感需求理解
            </Link>
          </div>

          {/* Category Description */}
          {currentCategory && (
            <div className="mb-10 p-5 bg-background-100 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className={`${currentCategory.icon} text-lg`}></i>
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground-900">{currentCategory.name}</h2>
              </div>
              <p className="text-sm text-foreground-600">{currentCategory.description}</p>
            </div>
          )}

          {/* Articles Grid */}
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">{t('category.noArticles')}</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}